/**
 * AI Question Queue — admin tab in /leadboard for reviewing AI-generated questions
 * from the Ask-a-CPA library (SEO-013).
 *
 * Workflow:
 *   1. Backend AI expander writes phrasings to gya_questions with status='pending_review'
 *   2. Admin opens this tab and sees them grouped by canonical parent
 *   3. Each entry has Approve / Reject (+ optional 1-word reason) actions
 *   4. Stats header shows pending count, today's approvals, top topics
 *
 * Endpoints used:
 *   GET  /api/v1/admin/get-your-answer/library/stats
 *   GET  /api/v1/admin/get-your-answer/library/review-queue?limit=...
 *   POST /api/v1/admin/get-your-answer/library/approve/{id}
 *   POST /api/v1/admin/get-your-answer/library/reject/{id}  body: {reason}
 *
 * Spec: SEO-013 in /app/docs/SEO_UI_CONSULTATION_LOG.md
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  RefreshCw,
  Sparkles,
  ChevronRight,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Ban,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GYAQueueTab({ sessionToken }) {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scope-rejection (ban/allow) panel — guards against prompt-injection and
  // off-topic content leaking into the public library. See
  // /app/backend/services/gya_scope_guard.py for the rules.
  const [scope, setScope] = useState({ items: [], stats: null });
  const [scopeRowState, setScopeRowState] = useState({});
  const [scopeNote, setScopeNote] = useState({});

  // Per-row local state: 'idle' | 'approving' | 'rejecting' | 'done' | 'error'
  const [rowState, setRowState] = useState({});
  // Per-row optional rejection reason
  const [rejectReason, setRejectReason] = useState({});

  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${sessionToken}`,
    'Content-Type': 'application/json',
  }), [sessionToken]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, q, sc] = await Promise.all([
        fetch(`${BACKEND}/api/v1/admin/get-your-answer/library/stats`, { headers: authHeaders }).then(r => r.json()),
        fetch(`${BACKEND}/api/v1/admin/get-your-answer/library/review-queue?limit=200`, { headers: authHeaders }).then(r => r.json()),
        fetch(`${BACKEND}/api/v1/admin/get-your-answer/scope-rejections?status_filter=open&limit=200`, { headers: authHeaders }).then(r => r.json()),
      ]);
      setStats(s);
      setQueue(q.items || []);
      setScope({ items: sc.items || [], stats: sc.stats || null });
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const approve = async (id) => {
    setRowState(s => ({ ...s, [id]: 'approving' }));
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/library/approve/${encodeURIComponent(id)}`, {
        method: 'POST', headers: authHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRowState(s => ({ ...s, [id]: 'done' }));
      // Remove from queue immediately for snappy UX
      setQueue(q => (q || []).filter(x => x.id !== id));
      // Update pending counter
      setStats(s => s ? { ...s, pending_review: Math.max(0, (s.pending_review || 0) - 1), published: (s.published || 0) + 1 } : s);
    } catch (e) {
      setRowState(s => ({ ...s, [id]: 'error' }));
    }
  };

  const reject = async (id) => {
    setRowState(s => ({ ...s, [id]: 'rejecting' }));
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/library/reject/${encodeURIComponent(id)}`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ reason: rejectReason[id] || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRowState(s => ({ ...s, [id]: 'done' }));
      setQueue(q => (q || []).filter(x => x.id !== id));
      setStats(s => s ? { ...s, pending_review: Math.max(0, (s.pending_review || 0) - 1), rejected: (s.rejected || 0) + 1 } : s);
    } catch (e) {
      setRowState(s => ({ ...s, [id]: 'error' }));
    }
  };

  // Scope-rejection actions — BAN keeps the rejection final (question gets
  // status=rejected permanently), ALLOW marks it as a false positive so the
  // next batch can re-process the question. These feed the prompt-injection
  // improvement loop: bans confirm patterns to keep in the block-list, and
  // allows tell us where the guard is too aggressive.
  const banScope = async (id) => {
    setScopeRowState(s => ({ ...s, [id]: 'banning' }));
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/scope-rejections/${encodeURIComponent(id)}/ban`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ note: scopeNote[id] || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setScopeRowState(s => ({ ...s, [id]: 'done' }));
      setScope(prev => ({ ...prev, items: (prev.items || []).filter(x => x.id !== id) }));
    } catch (e) {
      setScopeRowState(s => ({ ...s, [id]: 'error' }));
    }
  };

  const allowScope = async (id) => {
    setScopeRowState(s => ({ ...s, [id]: 'allowing' }));
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/scope-rejections/${encodeURIComponent(id)}/allow`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ note: scopeNote[id] || '' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setScopeRowState(s => ({ ...s, [id]: 'done' }));
      setScope(prev => ({ ...prev, items: (prev.items || []).filter(x => x.id !== id) }));
    } catch (e) {
      setScopeRowState(s => ({ ...s, [id]: 'error' }));
    }
  };

  // Group queue by parent canonical for visual chunking
  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of queue) {
      const k = item.parent_canonical_id || '__none__';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(item);
    }
    return Array.from(map.entries());
  }, [queue]);

  return (
    <div data-testid="gya-queue-tab">
      {/* ── Header / stats strip ── */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Pending review" value={stats?.pending_review ?? '—'} tone="warn" data-testid="gya-stat-pending" />
        <StatCard label="Published" value={stats?.published ?? '—'} tone="ok" data-testid="gya-stat-published" />
        <StatCard label="Rejected (all-time)" value={stats?.rejected ?? '—'} tone="muted" data-testid="gya-stat-rejected" />
        <StatCard label="AI-generated" value={stats?.ai_origin ?? '—'} tone="muted" data-testid="gya-stat-ai" />
        <StatCard label="Canonical questions" value={stats?.canonical_count ?? '—'} tone="muted" data-testid="gya-stat-canonical" />
      </div>

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-ivory flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            AI Question Review Queue
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Questions generated by{' '}
            <code className="text-[#D4AF37]">expand_questions_ai.py</code>{' '}
            await your approval before going live in the public library.
          </p>
        </div>
        <Button
          onClick={loadAll}
          disabled={loading}
          variant="outline"
          className="bg-navy-light border-gray-600 text-ivory hover:bg-navy-light/70"
          data-testid="gya-queue-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="bg-red-500/10 border-red-500/30 mb-4">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-300 text-sm">
              <AlertTriangle className="w-4 h-4" />
              Failed to load queue: {error}
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && queue.length === 0 && !error && (
        <Card className="bg-navy-light border-gray-700">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
            <p className="text-ivory font-semibold mb-1">Queue is empty.</p>
            <p className="text-gray-400 text-sm">
              Either nothing has been generated yet, or you&rsquo;ve approved everything.
              Run the expander from a shell:
            </p>
            <code className="block mt-3 text-xs text-[#D4AF37] bg-navy px-3 py-2 rounded mx-auto inline-block">
              python3 /app/scripts/expand_questions_ai.py --phrasings 15
            </code>
          </CardContent>
        </Card>
      )}

      {/* ── Grouped queue ── */}
      <div className="space-y-6">
        {(grouped || []).map(([canonicalId, items]) => (
          <Card key={canonicalId} className="bg-navy-light border-gray-700" data-testid={`gya-group-${canonicalId}`}>
            <CardContent className="pt-5">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                <span className="tracking-[0.18em] uppercase font-semibold text-[#D4AF37]">Canonical parent</span>
                <ChevronRight className="w-3 h-3" />
                <code className="text-gray-300">{canonicalId}</code>
                <span className="ml-auto text-gray-500">{items.length} phrasing{items.length === 1 ? '' : 's'} pending</span>
              </div>

              <ul className="space-y-3">
                {(items || []).map((item) => {
                  const state = rowState[item.id] || 'idle';
                  const reason = rejectReason[item.id] || '';
                  return (
                    <li
                      key={item.id}
                      className="p-3 rounded-lg bg-navy border border-gray-700 hover:border-gray-600 transition-colors"
                      data-testid={`gya-row-${item.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="text-ivory text-sm leading-snug">&ldquo;{item.text}&rdquo;</p>
                          <div className="mt-2 flex items-center gap-3 text-[10px] tracking-[0.12em] uppercase text-gray-500">
                            <span>{item.topic_label}</span>
                            <span>&middot;</span>
                            <span>{item.urgency}</span>
                            <span>&middot;</span>
                            <span>{item.model}</span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end shrink-0">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approve(item.id)}
                              disabled={state !== 'idle'}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white"
                              data-testid={`gya-approve-${item.id}`}
                            >
                              {state === 'approving' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => reject(item.id)}
                              disabled={state !== 'idle'}
                              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                              data-testid={`gya-reject-${item.id}`}
                            >
                              {state === 'rejecting' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                              Reject
                            </Button>
                          </div>
                          <Input
                            type="text"
                            value={reason}
                            onChange={(e) => setRejectReason(s => ({ ...s, [item.id]: e.target.value }))}
                            placeholder="Reject reason (optional)"
                            className="h-7 text-xs w-48 bg-navy-light border-gray-700 text-gray-300"
                            disabled={state !== 'idle'}
                            maxLength={60}
                            data-testid={`gya-reason-${item.id}`}
                          />
                          {state === 'error' && (
                            <span className="text-xs text-red-400">Failed — retry</span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Content review — auto-flagged items requiring admin decision ── */}
      <Card className="mt-8 bg-navy-light border-red-500/30" data-testid="gya-scope-panel">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-[0.18em] uppercase font-semibold text-red-300 flex items-center gap-2">
                <ShieldAlert className="w-4 h-4" />
                Content Review &mdash; Auto-Flagged Items
              </div>
              <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                Items the content checks blocked from publication (off-topic terms, missing tax keywords, formatting issues, or content the writer declined). BAN locks the question out permanently. ALLOW marks the flag as a false positive so the next pass re-processes it.
              </p>
            </div>
            {scope.stats && (
              <div className="flex gap-3 text-xs">
                <span className="px-2.5 py-1 rounded bg-red-500/10 text-red-300 border border-red-500/30">
                  {scope.stats.open ?? 0} open
                </span>
                <span className="px-2.5 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700">
                  {scope.stats.banned ?? 0} banned
                </span>
                <span className="px-2.5 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700">
                  {scope.stats.allowed ?? 0} allowed
                </span>
              </div>
            )}
          </div>

          {/* Top reasons strip — feeds the prompt-injection improvement loop */}
          {scope.stats?.by_reason && scope.stats.by_reason.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {scope.stats.by_reason.slice(0, 8).map(r => (
                <span
                  key={r.reason}
                  className="text-[10px] tracking-[0.12em] uppercase text-gray-400 bg-navy border border-gray-700 px-2 py-1 rounded"
                  data-testid={`gya-scope-reason-${r.reason}`}
                >
                  {r.reason}: <b className="text-red-300">{r.count}</b>
                </span>
              ))}
            </div>
          )}

          {scope.items.length === 0 ? (
            <div className="py-6 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              No open items. All clear.
            </div>
          ) : (
            <ul className="space-y-3">
              {(scope.items || []).map(item => {
                const state = scopeRowState[item.id] || 'idle';
                const note = scopeNote[item.id] || '';
                return (
                  <li
                    key={item.id}
                    className="p-3 rounded-lg bg-navy border border-red-500/20 hover:border-red-500/40 transition-colors"
                    data-testid={`gya-scope-row-${item.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-[10px] tracking-[0.14em] uppercase font-semibold text-red-300 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded">
                            {item.stage}
                          </span>
                          <span className="text-[10px] tracking-[0.14em] uppercase font-semibold text-gray-400 bg-gray-800 border border-gray-700 px-1.5 py-0.5 rounded">
                            {item.reason}
                          </span>
                          {item.topic_label && (
                            <span className="text-[10px] tracking-[0.14em] uppercase text-gray-500">
                              {item.topic_label}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-600 ml-auto">
                            {item.at ? new Date(item.at).toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-ivory text-sm leading-snug mb-1">
                          &ldquo;{item.question_text || '(no question text)'}&rdquo;
                        </p>
                        {item.detail && (
                          <p className="text-[11px] text-gray-500 mt-1">{item.detail}</p>
                        )}
                        {Array.isArray(item.matched) && item.matched.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {(item.matched || []).map((m, i) => (
                              <code key={i} className="text-[10px] text-red-300 bg-red-500/10 border border-red-500/30 px-1.5 py-0.5 rounded">
                                {m}
                              </code>
                            ))}
                          </div>
                        )}
                        {item.rejected_text && (
                          <details data-testid="g-y-a-queue-tab-details-1" className="mt-2">
                            <summary data-testid="g-y-a-queue-tab-summary-1" className="text-[11px] text-gray-500 cursor-pointer hover:text-gray-300">
                              Show rejected output
                            </summary>
                            <pre className="mt-1 text-[11px] text-gray-400 bg-navy-light p-2 rounded whitespace-pre-wrap max-h-32 overflow-auto">
                              {item.rejected_text}
                            </pre>
                          </details>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 items-end shrink-0">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => banScope(item.id)}
                            disabled={state !== 'idle'}
                            className="bg-red-600 hover:bg-red-500 text-white"
                            data-testid={`gya-scope-ban-${item.id}`}
                          >
                            {state === 'banning' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                            Ban
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => allowScope(item.id)}
                            disabled={state !== 'idle'}
                            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`gya-scope-allow-${item.id}`}
                          >
                            {state === 'allowing' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Allow
                          </Button>
                        </div>
                        <Input
                          type="text"
                          value={note}
                          onChange={(e) => setScopeNote(s => ({ ...s, [item.id]: e.target.value }))}
                          placeholder="Admin note (optional)"
                          className="h-7 text-xs w-48 bg-navy-light border-gray-700 text-gray-300"
                          disabled={state !== 'idle'}
                          maxLength={120}
                          data-testid={`gya-scope-note-${item.id}`}
                        />
                        {state === 'error' && (
                          <span className="text-xs text-red-400">Failed — retry</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Top-clicked widget ── */}
      {stats?.top_clicked && stats.top_clicked.length > 0 && (
        <Card className="mt-8 bg-navy-light border-gray-700">
          <CardContent className="pt-5">
            <div className="text-xs tracking-[0.18em] uppercase font-semibold text-[#D4AF37] mb-3">
              Top-clicked questions (live library)
            </div>
            <ul className="space-y-2">
              {(stats.top_clicked || []).map(t => (
                <li key={t.id} className="flex items-center gap-3 text-sm text-gray-300">
                  <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30">{t.clicks} clicks</Badge>
                  <span className="flex-1 truncate">{t.text}</span>
                  <span className="text-xs text-gray-500">{t.hits} hits</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'muted', ...rest }) {
  const toneClass = {
    warn:   'border-[#D4AF37]/40 text-[#D4AF37]',
    ok:     'border-emerald-500/30 text-emerald-300',
    muted:  'border-gray-700 text-gray-300',
  }[tone] || 'border-gray-700';
  return (
    <Card className={`bg-navy-light ${toneClass}`} {...rest}>
      <CardContent className="pt-4 pb-4">
        <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

